import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APP } from 'Constants/App'
import { LangError } from 'Types/app'
import httpClient from 'Service'
import { toast } from 'react-toastify'

export type CenterHistoryItem = {
  historyId: number
  year: string
  titleUz: string | null
  titleRu: string | null
  titleKo: string | null
  descriptionUz: string | null
  descriptionRu: string | null
  descriptionKo: string | null
  sortOrder?: number
}

export type HistoryFormValues = {
  year: string
  titleUz: string
  titleRu: string
  titleKo: string
  descriptionUz: string
  descriptionRu: string
  descriptionKo: string
}

type UpdateHistoryParams = {
  id: number
  entries: HistoryFormValues
}

const getCenterHistories = async (): Promise<CenterHistoryItem[]> => {
  const response = await httpClient.get('/api/org/history')
  return response.data
}

const transformFromBackendFormat = (item: CenterHistoryItem): HistoryFormValues => {
  return {
    year: item.year ?? '',
    titleUz: item.titleUz ?? '',
    titleRu: item.titleRu ?? '',
    titleKo: (item as any).titleKo ?? '',
    descriptionUz: item.descriptionUz ?? '',
    descriptionRu: item.descriptionRu ?? '',
    descriptionKo: (item as any).descriptionKo ?? '',
  }
}

const createHistory = async (entries: HistoryFormValues): Promise<CenterHistoryItem> => {
  const response = await httpClient.post('/api/account/history', entries)
  return response.data
}

const updateHistory = async ({ id, entries }: UpdateHistoryParams): Promise<CenterHistoryItem> => {
  const response = await httpClient.put(`/api/account/history/${id}`, entries)
  return response.data
}

const deleteHistory = async (id: number) => {
  const response = await httpClient.delete(`/api/account/history/${id}`)
  return response.data
}

const defaultValues: HistoryFormValues = {
  year: '',
  titleUz: '',
  titleRu: '',
  titleKo: '',
  descriptionUz: '',
  descriptionRu: '',
  descriptionKo: '',
}

const normalizeValue = (value?: string | null) => (value || '').trim()

const toFormValues = (item: CenterHistoryItem | null): HistoryFormValues => {
  if (!item) return defaultValues
  return transformFromBackendFormat(item)
}

const normalizeFormValues = (values: HistoryFormValues): HistoryFormValues => ({
  year: normalizeValue(values.year),
  titleUz: normalizeValue(values.titleUz),
  titleRu: normalizeValue(values.titleRu),
  titleKo: normalizeValue(values.titleKo),
  descriptionUz: normalizeValue(values.descriptionUz),
  descriptionRu: normalizeValue(values.descriptionRu),
  descriptionKo: normalizeValue(values.descriptionKo),
})

const isSameHistoryValues = (currentItem: CenterHistoryItem | null, nextValues: HistoryFormValues) => {
  if (!currentItem) return false

  const current = normalizeFormValues(toFormValues(currentItem))
  const next = normalizeFormValues(nextValues)

  return (
    current.year === next.year &&
    current.titleUz === next.titleUz &&
    current.titleRu === next.titleRu &&
    current.titleKo === next.titleKo &&
    current.descriptionUz === next.descriptionUz &&
    current.descriptionRu === next.descriptionRu &&
    current.descriptionKo === next.descriptionKo
  )
}

export function useCenterHistoryForm2() {
  const queryClient = useQueryClient()

  const listQuery = useQuery<CenterHistoryItem[]>({
    queryFn: getCenterHistories,
    queryKey: ['center-history'],
  })

  const histories = useMemo<CenterHistoryItem[]>(() => {
    return Array.isArray(listQuery.data) ? listQuery.data : []
  }, [listQuery.data])

  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null)
  const [isCreateMode, setIsCreateMode] = useState(false)

  const schema = yup.object({
    year: yup.string().trim().required(APP.REQUIRED_FIELD),
    titleUz: yup.string().trim().required(APP.REQUIRED_FIELD),
    titleRu: yup.string().trim().required(APP.REQUIRED_FIELD),
    titleKo: yup.string().trim().required(APP.REQUIRED_FIELD),
    descriptionUz: yup.string().trim().notRequired(),
    descriptionRu: yup.string().trim().notRequired(),
    descriptionKo: yup.string().trim().notRequired(),
  })

  const form = useForm<HistoryFormValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  })

  const {
    formState: { errors },
    reset,
    watch,
  } = form

  const watchedValues = watch()

  const selectedHistory = useMemo(() => {
    if (!selectedHistoryId) return null
    return histories.find(item => item.historyId === selectedHistoryId) ?? null
  }, [histories, selectedHistoryId])

  const create = useMutation({
    mutationFn: createHistory,
    onSuccess: async createdItem => {
      toast.success('History created successfully')
      await queryClient.invalidateQueries({ queryKey: ['center-history'] })

      if (createdItem?.historyId) {
        setSelectedHistoryId(createdItem.historyId)
        setIsCreateMode(false)
      }
    },
    onError: () => {
      toast.error('Failed to create history')
    },
  })

  const update = useMutation({
    mutationFn: updateHistory,
    onSuccess: async updatedItem => {
      toast.success('History updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['center-history'] })

      if (updatedItem?.historyId) {
        setSelectedHistoryId(updatedItem.historyId)
        setIsCreateMode(false)
      }
    },
    onError: () => {
      toast.error('Failed to update history')
    },
  })

  const deleteHist = useMutation({
    mutationFn: deleteHistory,
    onSuccess: async (_, deletedId) => {
      toast.success('History deleted successfully')

      await queryClient.invalidateQueries({ queryKey: ['center-history'] })

      const current = queryClient.getQueryData<CenterHistoryItem[]>(['center-history'])
      const remained = (current ?? []).filter(item => item.historyId !== deletedId)

      if (remained.length > 0) {
        setSelectedHistoryId(remained[0].historyId)
        setIsCreateMode(false)
      } else {
        setSelectedHistoryId(null)
        setIsCreateMode(true)
        reset(defaultValues)
      }
    },
    onError: () => {
      toast.error('Failed to delete history')
    },
  })

  const fillForm = useCallback(
    (item: CenterHistoryItem | null) => {
      reset(toFormValues(item), {
        keepDirty: false,
        keepTouched: false,
      })
    },
    [reset],
  )

  const handleCreateNew = useCallback(() => {
    setIsCreateMode(true)
    setSelectedHistoryId(null)
    reset(defaultValues)
  }, [reset])

  const handleSelectHistory = useCallback((historyId: number) => {
    setIsCreateMode(false)
    setSelectedHistoryId(historyId)
  }, [])

  const handleEditClick = useCallback((historyId: number) => {
    setIsCreateMode(false)
    setSelectedHistoryId(historyId)
  }, [])

  const handleDelete = useCallback(
    (historyId?: number) => {
      const targetId = historyId ?? selectedHistoryId
      if (!targetId) return
      deleteHist.mutate(targetId)
    },
    [deleteHist, selectedHistoryId],
  )

  const onSubmit = useCallback(
    (values: HistoryFormValues) => {
      const payload = normalizeFormValues(values)

      if (isCreateMode) {
        create.mutate(payload)
        return
      }

      if (!selectedHistoryId || !selectedHistory) return

      if (isSameHistoryValues(selectedHistory, payload)) {
        toast.warning('No changes were made')
        return
      }

      update.mutate({
        id: selectedHistoryId,
        entries: payload,
      })
    },
    [create, isCreateMode, selectedHistoryId, selectedHistory, update],
  )

  const langError: Partial<LangError> = useMemo(
    () => ({
      Ru: !!errors.titleRu?.message,
      Uz: !!errors.titleUz?.message,
      Ko: !!errors.titleKo?.message,
    }),
    [errors],
  )

  const allFieldsFilled = useMemo(() => {
    const values = normalizeFormValues(watchedValues as HistoryFormValues)

    return (
      !!values.year &&
      !!values.titleUz &&
      !!values.titleRu &&
      !!values.titleKo
    )
  }, [watchedValues])

  const isSubmitting = create.isLoading || update.isLoading
  const isDeleting = deleteHist.isLoading

  useEffect(() => {
    if (!histories.length) {
      setIsCreateMode(true)
      reset(defaultValues)
      return
    }

    if (isCreateMode) return

    if (!selectedHistoryId) {
      setSelectedHistoryId(histories[0].historyId)
      return
    }

    const exists = histories.some(item => item.historyId === selectedHistoryId)
    if (!exists) {
      setSelectedHistoryId(histories[0].historyId)
    }
  }, [histories, isCreateMode, reset, selectedHistoryId])

  useEffect(() => {
    if (isCreateMode) {
      reset(defaultValues)
      return
    }

    fillForm(selectedHistory)
  }, [fillForm, isCreateMode, reset, selectedHistory])

  return {
    form,
    onSubmit,
    langError,
    allFieldsFilled,
    isSubmitting,
    histories,
    selectedHistoryId,
    isCreateMode,
    handleSelectHistory,
    handleCreateNew,
    handleDelete,
    handleEditClick,
    isDeleting,
  }
}
